import { TextField } from '@mui/material';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { DialogType, SelectOption } from '../../../@types';
import { CasinoSummaryFilters } from '../../../@types/casino';
import { BETTING_ACTIVITY_OPTIONS } from '../../../constants';
import { getDefaultCasinoReportRole, prepareCasinoReportFilters } from '../../../helpers/casino';
import { useCasinoProviderOptions } from '../../../hooks/useCasinoProviderOptions';
import useFilterForm from '../../../hooks/useFilterForm';
import usePermissions from '../../../hooks/usePermissions';
import { AdditionalQueryParams } from '../../../hooks/usePagination';
import FilterDialogLayout from '../../layouts/FilterDialogLayout';
import FormSelect from '../../molecules/FormSelect';
import CasinoRoleFilters from './CasinoRoleFilters';

type FilterCasinoReportsData = Omit<CasinoSummaryFilters, 'dateFrom' | 'dateTo'>;

const ENTITY_OPTIONS: SelectOption[] = [
  { id: '', name: 'All entities' },
  { id: 'ONLINE', name: 'Online' },
  { id: 'RETAIL', name: 'Retail' },
];

type Props = DialogType & {
  changeQuery: (data: AdditionalQueryParams) => void;
};

const FilterCasinoReports = ({ changeQuery, closeModal }: Props) => {
  const { role: userRole } = usePermissions();
  const { options: providerOptions, isLoading: providersLoading } = useCasinoProviderOptions();

  const defaultFormData = useMemo<FilterCasinoReportsData>(
    () => ({
      role: getDefaultCasinoReportRole(userRole),
      managerIds: undefined,
      agentIds: undefined,
      playerIds: undefined,
      entityId: undefined,
      providerName: undefined,
      gameName: undefined,
      bettingActivity: 'ACTIVE',
    }),
    [userRole]
  );

  const { control, handleResetFilter, handleSubmit, watch } = useFilterForm<FilterCasinoReportsData>({
    defaultData: defaultFormData,
    changeQuery,
    prepareQueryParams: prepareCasinoReportFilters,
  });

  return (
    <FilterDialogLayout
      label='casino reports'
      onSave={handleSubmit}
      onClose={() => closeModal?.()}
      onReset={handleResetFilter}
    >
      <Controller
        control={control}
        name='gameName'
        render={({ field }) => <TextField {...field} value={field.value ?? ''} label='Game name' />}
      />
      <FormSelect
        control={control}
        name='providerName'
        label='Provider'
        options={providerOptions}
        disabled={providersLoading && !providerOptions.length}
      />
      <FormSelect control={control} name='entityId' label='Entity' options={ENTITY_OPTIONS} />
      <CasinoRoleFilters control={control} watch={watch} roleName='role' />
      <FormSelect
        control={control}
        name='bettingActivity'
        label='Betting Activity'
        options={BETTING_ACTIVITY_OPTIONS}
      />
    </FilterDialogLayout>
  );
};

export default FilterCasinoReports;
